import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { FileText, NotebookPen, ArrowRight, Search } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function CaseFiles() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/notes/clients").then((r) => setClients(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const allowed = user?.role === "counsellor" || user?.role === "admin";

  const filtered = clients.filter((c) =>
    !q.trim() || `${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase())
  );

  if (!allowed) {
    return <div className="min-h-screen"><Navbar /><main className="max-w-2xl mx-auto px-6 py-12 text-center"><h1 className="text-2xl font-extrabold text-[#3D405B]">Insufficient permissions</h1></main></div>;
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <header className="brand-card p-7 mb-6">
          <div className="overline mb-2 flex items-center gap-2"><NotebookPen size={13} /> Clinical case files</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3D405B] tracking-tight">Your client records</h1>
          <p className="text-[#7A7D96] mt-2">Open a client to view their full case file, add notes or export a POPIA-safe PDF.</p>
          <div className="relative mt-5 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A7D96]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" data-testid="case-files-search" />
          </div>
        </header>

        {loading ? (
          <div className="text-center py-16 text-[#7A7D96]">Loading clients…</div>
        ) : filtered.length === 0 ? (
          <div className="brand-card p-12 text-center">
            <FileText className="mx-auto text-[#7A7D96]/60 mb-3" size={42} />
            <h3 className="font-extrabold text-[#3D405B] text-lg">{q ? "No matches" : "No clients yet"}</h3>
            <p className="text-sm text-[#7A7D96] mt-1">{q ? "Try a different search term." : "Once a client books a session with you, they'll show up here."}</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((c) => (
              <li key={c.user_id}>
                <Link to={`/case-file/${c.user_id}`} className="brand-card brand-card-hover p-5 flex items-center gap-4 transition-all" data-testid={`case-files-row-${c.user_id}`}>
                  <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center font-extrabold text-[#3D405B]">{c.name?.[0] || "?"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-[#3D405B] truncate">{c.name}</div>
                    <div className="text-xs text-[#7A7D96] truncate">{c.email}</div>
                    <div className="text-xs text-[#7A7D96] mt-1">
                      <span className="font-bold text-[#3D405B]">{c.note_count}</span> entr{c.note_count === 1 ? "y" : "ies"}
                      {c.latest_note_at && <> · last {fmt(c.latest_note_at)}</>}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-primary" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
